import React, { FC } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import { secondsToHours } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import Dropdown from '@components/common/dropdown';
import { DashboardScope, DashboardResultType } from '@api/domain/types';
import type { DashboardOverallResponse } from '@api/domain/types';

import DashboardScopeTabs from '@v2/pages/dashboard/common/components/DashboardScopeTabs';
import { DASHBOARD_RESULT_TYPE_OPTIONS } from '@v2/pages/dashboard/common/constants/selections';
import { DASHBOARD_PRESS_HOVER_BG_COLOR } from '@v2/pages/dashboard/common/constants/colors';
import { useManageDashboardOverviewTopBlock } from './hooks/useManageDashboardOverviewTopBlock';
import DashboardOverviewTopBlockSummaryItem from './components/DashboardOverviewTopBlockSummaryItem';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { ContainerStyle } from '@interfaces/base';
import Svg from '@styles/icons/svg';
import DashboardDropdownSelectedIcon from '@v2/pages/dashboard/common/components/DashboardDropdownSelectedIcon';
import { DROPDOWN_CONFIG } from '@v2/pages/dashboard/common/constants/dropdown';

export interface DashboardOverviewTopBlockProps extends WithColor, ContainerStyle {
  dashboardOverallData: DashboardOverallResponse | null;
  scope: DashboardScope;
  onSelectScope: (scope: DashboardScope) => void;
  resultType: DashboardResultType;
  onSelectResultType: (resultType: DashboardResultType) => void;
}

const DashboardOverviewTopBlock: FC<DashboardOverviewTopBlockProps> = ({
  C,
  dashboardOverallData,
  scope,
  onSelectScope,
  resultType,
  onSelectResultType,
  containerStyle,
}) => {
  const { positiveRatio, negativeSeconds, negativeCount, positiveSeconds, positiveCount, netSeconds, isLoss, accentColor, triggerColor, triggerLabel } =
    useManageDashboardOverviewTopBlock({ dashboardOverallData, resultType });

  const containerBoxShadowStyles = getBoxShadowStyle({ C, type: 'Narrow' });

  return (
    <View
      testID={TEST_IDS.dashboardOverview.topBlock}
      style={[
        containerBoxShadowStyles,
        {
          backgroundColor: C('Background/Light/System'),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          padding: scale(16),
        },
        containerStyle,
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: scale(8) }}>
        <View
          style={{
            width: scale(4),
            height: scale(16),
            backgroundColor: C(triggerColor),
            marginRight: scale(6),
            borderRadius: scale(2),
          }}
        />
        <Dropdown
          items={DASHBOARD_RESULT_TYPE_OPTIONS}
          selectedValue={resultType}
          onChange={onSelectResultType}
          menuAlignment="left"
          menuStyle={{ width: undefined, minWidth: scale(DROPDOWN_CONFIG.minWidth) }}
          optionStyle={{ height: scale(40) }}
          withOptionDivider
          optionTextStyle={({ isSelected }) => ({
            ...font[isSelected ? 'Subhead 1 bold' : 'Subhead 2'],
            color: C(isSelected ? 'System/Light/Blue' : 'Label/Light/Primary'),
          })}
          selectedIcon={<DashboardDropdownSelectedIcon />}
          testIDForOption={(value) => TEST_IDS.dashboardOverview.resultTypeOption(value)}
          renderTrigger={({ triggerRef, onPress }) => (
            <TouchableElement testID={TEST_IDS.dashboardOverview.resultTypeTrigger} onPress={onPress}>
              {({ boxStyle, pressHoverColor, hovered, pressed }) => (
                <View
                  ref={triggerRef}
                  style={[
                    boxStyle,
                    {
                      paddingVertical: scale(4),
                      paddingHorizontal: scale(8),
                      gap: scale(4),
                      flexDirection: 'row',
                      alignItems: 'center',
                      borderRadius: scale(8),
                    },
                    (hovered || pressed) && {
                      backgroundColor: C(pressHoverColor(DASHBOARD_PRESS_HOVER_BG_COLOR)),
                    },
                  ]}
                >
                  <Text style={[font['Button 2'], { color: C(triggerColor) }]}>{i18n.t(triggerLabel)}</Text>
                  <Svg icon="chevronBottom" color={triggerColor} width={scale(15)} height={scale(16)} />
                </View>
              )}
            </TouchableElement>
          )}
        />
      </View>

      <View style={{ alignItems: 'center', marginBottom: scale(12) }}>
        <Text style={[font['Title 1'], { color: C(accentColor) }]}>{secondsToHours(netSeconds)}</Text>
        <Text style={[font['Caption 3'], { color: C(accentColor) }]}>{i18n.t(isLoss ? 'more time used than paid' : 'less time used than paid')}</Text>
      </View>

      <DashboardScopeTabs
        scope={scope}
        onSelectScope={onSelectScope}
        testIDForTab={(value) => TEST_IDS.dashboardOverview.scopeTab(value)}
        containerStyle={{ marginBottom: scale(20) }}
      />

      <View
        style={{
          flexDirection: 'row',
          height: scale(4),
          borderRadius: scale(2),
          overflow: 'hidden',
          marginBottom: scale(8),
          backgroundColor: C('Background/Light/Tertiary'),
        }}
      >
        <View style={{ flex: 1 - positiveRatio, backgroundColor: C('System/Light/Red') }} />
        <View style={{ flex: positiveRatio, backgroundColor: C('System/Light/Green') }} />
      </View>

      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <DashboardOverviewTopBlockSummaryItem color="System/Light/Red" seconds={negativeSeconds} count={negativeCount} label="with loss" />
        <DashboardOverviewTopBlockSummaryItem color="System/Light/Green" seconds={positiveSeconds} count={positiveCount} label="with loss" />
      </View>
    </View>
  );
};

export default ColorWrapper(DashboardOverviewTopBlock);
